import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Edit, Lock, MapPin, Plus, Star, Trash2, Upload, XCircle } from "lucide-react";
import {
  GOOGLE_REVIEW_ORIGINS,
  canImportGoogleReviews,
  type GoogleReviewAdminItem,
  type GoogleReviewOrigin,
} from "@ckgo2/shared";
import { useAuthStore } from "@/stores/authStore";
import { formatDate } from "@/lib/format";
import ConfirmModal from "@/components/common/ConfirmModal";
import ErrorBanner from "@/components/common/ErrorBanner";
import TakeoutUploadPanel from "@/components/googleReviews/TakeoutUploadPanel";
import DestinationCorrectionDialog from "@/components/googleReviews/DestinationCorrectionDialog";
import { useDestinationOptions } from "@/components/googleReviews/useDestinationOptions";
import {
  GOOGLE_REVIEW_DESTINATION_NONE,
  deleteManualGoogleReview,
  googleReviewsKeys,
  removeGoogleReviewPermanently,
  useGoogleReviewsList,
  type GoogleReviewListResponse,
} from "@/components/googleReviews/googleReviewsApi";

const DANGER_ICON_BUTTON =
  "btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20";

type OriginLabels = Record<GoogleReviewOrigin, string>;

/** Mazací akce řádku; obě se potvrzují týmž dialogem a liší se jen voláním a texty. */
type RowRemoval = "delete" | "remove";

/** Otevřený dialog řádku: nejvýš jeden, proto jeden rozlišený stav místo tří. */
type RowDialog =
  | { kind: RowRemoval; item: GoogleReviewAdminItem }
  | { kind: "correct"; item: GoogleReviewAdminItem };

const REMOVAL_CALLS: Record<RowRemoval, (id: string) => Promise<unknown>> = {
  delete: deleteManualGoogleReview,
  remove: removeGoogleReviewPermanently,
};

/**
 * Google recenze v adminu (U6, F2.9): seznam s filtrem podle původu a destinace, nahrání
 * exportu Takeout (jen `canImportGoogleReviews`, týž zdroj rolí jako brána serveru), ruční
 * zadání, oprava destinace, smazání ruční a odstranění natrvalo.
 *
 * Filtr i stránkování počítá API (každá volba = nový `GET` s parametrem); v paměti se nic
 * nefiltruje ani neřadí. Zápisové akce vidí každá role, bránu (readonly 403) drží server.
 * Mazání se potvrzuje dialogem: klik v řádku jen otevře dialog, požadavek odchází
 * výhradně z `onConfirm`.
 */
export default function GoogleReviewsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const canImport = canImportGoogleReviews(role);

  const [showUpload, setShowUpload] = useState(false);
  const [page, setPage] = useState(1);
  const [origin, setOrigin] = useState<GoogleReviewOrigin | null>(null);
  const [destinationId, setDestinationId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<RowDialog | null>(null);

  const listQuery = useGoogleReviewsList({ page, origin, destinationId });
  const meta = listQuery.data?.meta;

  // Po zápisu obnovit seznam i souhrn. Zmizel-li poslední řádek stránky za první,
  // vrátit se o stránku zpět, jinak by zůstala prázdná tabulka při `total > 0`.
  const afterRowGone = () => {
    if (page > 1 && listQuery.data?.data.length === 1) setPage(page - 1);
    return queryClient.invalidateQueries({ queryKey: googleReviewsKeys.all });
  };

  // Chyba (readonly 403, 409 u odstraněné) patří do pruhu nad tabulkou, ne pod dialog,
  // proto se dialog zavře i po chybě.
  const removalMutation = useMutation({
    mutationFn: ({ kind, id }: { kind: RowRemoval; id: string }) => REMOVAL_CALLS[kind](id),
    onSuccess: afterRowGone,
    onSettled: () => setDialog(null),
  });
  const openRemoval = (kind: RowRemoval) => (item: GoogleReviewAdminItem) => {
    removalMutation.reset();
    setDialog({ kind, item });
  };
  const removalTexts: Record<RowRemoval, { title: string; message: string; confirm: string }> = {
    delete: {
      title: t("content.googleReviews.list.deleteTitle"),
      message: t("content.googleReviews.list.deleteMessage"),
      confirm: t("content.googleReviews.list.deleteConfirm"),
    },
    remove: {
      title: t("content.googleReviews.list.removeTitle"),
      message: t("content.googleReviews.list.removeMessage"),
      confirm: t("content.googleReviews.list.removeConfirm"),
    },
  };
  const removal = dialog?.kind === "correct" ? null : dialog;

  const originLabels: OriginLabels = {
    export: t("content.googleReviews.origin.export"),
    manual: t("content.googleReviews.origin.manual"),
  };
  const hasFilter = origin !== null || destinationId !== null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("content.googleReviews.list.title")}
          </h1>
          {meta && (
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {t("content.googleReviews.list.count", { count: meta.total })}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          {canImport && (
            <button type="button" onClick={() => setShowUpload(true)} className="btn-primary">
              <Upload className="h-5 w-5" />
              {t("content.googleReviews.list.uploadExport")}
            </button>
          )}
          <Link to="/google-reviews/new" className="btn-secondary">
            <Plus className="h-5 w-5" />
            {t("content.googleReviews.list.addManual")}
          </Link>
        </div>
      </div>

      {canImport && showUpload && <TakeoutUploadPanel onClose={() => setShowUpload(false)} />}

      <GoogleReviewsFilters
        origin={origin}
        destinationId={destinationId}
        originLabels={originLabels}
        onOrigin={(value) => {
          setOrigin(value);
          setPage(1);
        }}
        onDestination={(value) => {
          setDestinationId(value);
          setPage(1);
        }}
      />

      <ErrorBanner error={removalMutation.error} />

      {listQuery.isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
        </div>
      ) : listQuery.isError || !listQuery.data ? (
        <div className="card text-error-600 text-center">
          {t("content.googleReviews.list.loadError")}
        </div>
      ) : listQuery.data.meta.total === 0 ? (
        <div className="card flex h-64 flex-col items-center justify-center text-gray-500">
          <Star className="mb-2 h-12 w-12 text-gray-300" />
          <p>
            {hasFilter
              ? t("content.googleReviews.list.emptyFiltered")
              : t("content.googleReviews.list.emptyAll")}
          </p>
        </div>
      ) : (
        <GoogleReviewsTable
          list={listQuery.data}
          page={page}
          originLabels={originLabels}
          onPage={setPage}
          onCorrect={(item) => setDialog({ kind: "correct", item })}
          onDelete={openRemoval("delete")}
          onRemove={openRemoval("remove")}
        />
      )}

      {removal && (
        <ConfirmModal
          isOpen
          onClose={() => setDialog(null)}
          onConfirm={() => removalMutation.mutate({ kind: removal.kind, id: removal.item.id })}
          isLoading={removalMutation.isPending}
          title={removalTexts[removal.kind].title}
          message={removalTexts[removal.kind].message}
          confirmText={removalTexts[removal.kind].confirm}
          cancelText={t("content.googleReviews.list.cancel")}
          variant="danger"
        />
      )}

      {dialog?.kind === "correct" && (
        <DestinationCorrectionDialog item={dialog.item} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}

function GoogleReviewsFilters({
  origin,
  destinationId,
  originLabels,
  onOrigin,
  onDestination,
}: {
  origin: GoogleReviewOrigin | null;
  destinationId: string | null;
  originLabels: OriginLabels;
  onOrigin: (value: GoogleReviewOrigin | null) => void;
  onDestination: (value: string | null) => void;
}) {
  const { t } = useTranslation();
  const destinationOptions = useDestinationOptions();
  return (
    <div className="card">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="sm:w-56">
          <label htmlFor="google-reviews-origin" className="label">
            {t("content.googleReviews.list.filterOrigin")}
          </label>
          <select
            id="google-reviews-origin"
            value={origin ?? ""}
            onChange={(e) =>
              onOrigin(GOOGLE_REVIEW_ORIGINS.find((o) => o === e.target.value) ?? null)
            }
            className="input"
          >
            <option value="">{t("content.googleReviews.list.filterOriginAll")}</option>
            {GOOGLE_REVIEW_ORIGINS.map((o) => (
              <option key={o} value={o}>
                {originLabels[o]}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:w-72">
          <label htmlFor="google-reviews-destination" className="label">
            {t("content.googleReviews.list.filterDestination")}
          </label>
          <select
            id="google-reviews-destination"
            value={destinationId ?? ""}
            onChange={(e) => onDestination(e.target.value === "" ? null : e.target.value)}
            className="input"
          >
            <option value="">{t("content.googleReviews.list.filterDestinationAll")}</option>
            <option value={GOOGLE_REVIEW_DESTINATION_NONE}>
              {t("content.googleReviews.list.filterDestinationNone")}
            </option>
            {destinationOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/** Tabulka jedné stránky seznamu; stránky a počet bere z `meta` odpovědi serveru. */
function GoogleReviewsTable({
  list,
  page,
  originLabels,
  onPage,
  onCorrect,
  onDelete,
  onRemove,
}: {
  list: GoogleReviewListResponse;
  page: number;
  originLabels: OriginLabels;
  onPage: (page: number) => void;
  onCorrect: (review: GoogleReviewAdminItem) => void;
  onDelete: (review: GoogleReviewAdminItem) => void;
  onRemove: (review: GoogleReviewAdminItem) => void;
}) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, list.meta.totalPages);
  const th = "px-4 py-3 font-medium";
  return (
    <div className="card overflow-x-auto p-0">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400">
          <tr>
            <th className={th}>{t("content.googleReviews.list.colAuthor")}</th>
            <th className={th}>{t("content.googleReviews.list.colDate")}</th>
            <th className={th}>{t("content.googleReviews.list.colExcerpt")}</th>
            <th className={th}>{t("content.googleReviews.list.colDestination")}</th>
            <th className={th}>{t("content.googleReviews.list.colOrigin")}</th>
            <th className={`${th} text-right`}>{t("content.googleReviews.list.colActions")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {list.data.map((review) => (
            <tr key={review.id} className="align-top">
              <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                {review.authorName}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-400">
                {/* Den UTC: ruční recenze nese půlnoc UTC, v záporném pásmu by ukázala den předem. */}
                {formatDate(review.reviewDate, { timeZone: "UTC" })}
              </td>
              <td className="max-w-md px-4 py-3 text-gray-600 dark:text-gray-400">
                <p className="line-clamp-2">{review.excerpt}</p>
              </td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                <span className="inline-flex items-center gap-1">
                  {review.destinationName ?? t("content.googleReviews.list.noDestination")}
                  {review.destinationLocked && (
                    <span title={t("content.googleReviews.list.lockedTitle")}>
                      <Lock
                        className="h-3.5 w-3.5 text-gray-400"
                        aria-label={t("content.googleReviews.list.lockedTitle")}
                      />
                    </span>
                  )}
                </span>
              </td>
              <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                {originLabels[review.origin]}
              </td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => onCorrect(review)}
                    className="btn-ghost btn-sm"
                    title={t("content.googleReviews.list.actionFixDestination")}
                    aria-label={t("content.googleReviews.list.actionFixDestination")}
                  >
                    <MapPin className="h-4 w-4" />
                  </button>
                  {review.origin === "manual" && (
                    <>
                      <Link
                        to={`/google-reviews/${review.id}`}
                        className="btn-ghost btn-sm"
                        title={t("content.googleReviews.list.actionEdit")}
                        aria-label={t("content.googleReviews.list.actionEdit")}
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => onDelete(review)}
                        className={DANGER_ICON_BUTTON}
                        title={t("content.googleReviews.list.actionDelete")}
                        aria-label={t("content.googleReviews.list.actionDelete")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => onRemove(review)}
                    className={DANGER_ICON_BUTTON}
                    title={t("content.googleReviews.list.actionRemovePermanently")}
                    aria-label={t("content.googleReviews.list.actionRemovePermanently")}
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm dark:border-gray-700">
        <button
          type="button"
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="btn-ghost btn-sm"
        >
          {t("content.googleReviews.list.pagePrev")}
        </button>
        <span className="text-gray-600 dark:text-gray-400">
          {t("content.googleReviews.list.pageOf", { page: list.meta.page, total: totalPages })}
        </span>
        <button
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          className="btn-ghost btn-sm"
        >
          {t("content.googleReviews.list.pageNext")}
        </button>
      </div>
    </div>
  );
}
